import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getHomeRouteByRole } from "../../utils/rutas";
import { API_URL } from "../../config";
import { postAccionesUsuarioBatch } from "../../utils/accionesUsuarioBatch";
import { unwrapAccionesListPayload } from "../../utils/unwrapAccionesListPayload";
import { getAccionSistemaTipoLabel } from "../../utils/accionSistemaLabels";
import {
  getAccionTipoFilterOptions,
  getRolPerfilFilterOptions,
} from "../../utils/accionesFiltroCatalog";
import { sortAccionesByFechaDesc, applyAccionesFiltros } from "../../utils/accionesListadoUtils";
import {
  buildAccionAffectedProfileLine,
  buildAccionCardHeadline,
  buildAccionDetailBody,
} from "../../utils/activityLogAccionCard";
import { formatActivityLogDateCell } from "../../utils/formatActivityLogDate";
import { getAutorizacionCursoLabel } from "../../utils/autorizacionCursoLabel";
import AccionesFiltroPanel from "../../components/AccionesFiltroPanel";
import ActivityRecordsListScroller from "../../components/ActivityRecordsListScroller";
import ResponsiveActionSidebar from "../../components/ResponsiveActionSidebar";
import es from "../../data/locales/es.json";
import "./Autorizaciones.css";

const activityUi = es.activityLog;

const DEFAULT_ACT_FILTROS = {
  nombre: "",
  accionTipo: "",
  fechaDesde: "",
  fechaHasta: "",
  rol: "",
};

const Autorizaciones = () => {
  const navigate = useNavigate();
  const [acciones, setAcciones] = useState([]);
  const [seleccionados, setSeleccionados] = useState([]);
  const [mensaje, setMensaje] = useState("");
  const [modalObservacion, setModalObservacion] = useState({ visible: false, accion: null });
  const [rol, setRol] = useState("");
  const [solicitudes, setSolicitudes] = useState([]);
  const [msgCursos, setMsgCursos] = useState("");
  const [filtrosDraft, setFiltrosDraft] = useState(DEFAULT_ACT_FILTROS);
  const [filtrosApplied, setFiltrosApplied] = useState(DEFAULT_ACT_FILTROS);

  const tipoOptions = useMemo(
    () => getAccionTipoFilterOptions(activityUi.filterTodosTipos),
    [],
  );
  const rolOptions = useMemo(
    () => getRolPerfilFilterOptions(activityUi.filterTodosRoles, activityUi.roles),
    [],
  );

  const accionesVisibles = useMemo(
    () => applyAccionesFiltros(sortAccionesByFechaDesc(acciones), filtrosApplied),
    [acciones, filtrosApplied],
  );

  useEffect(() => {
    const visible = new Set(accionesVisibles.map((a) => String(a._id)));
    setSeleccionados((prev) => prev.filter((id) => visible.has(String(id))));
  }, [accionesVisibles]);

  const handleApplyFiltros = useCallback(() => {
    setFiltrosApplied({ ...filtrosDraft });
    setSeleccionados([]);
  }, [filtrosDraft]);

  const handleClearFiltros = useCallback(() => {
    setFiltrosDraft(DEFAULT_ACT_FILTROS);
    setFiltrosApplied(DEFAULT_ACT_FILTROS);
    setSeleccionados([]);
  }, []);

  const filterPanelLabels = {
    nombre: activityUi.listFilterNombre,
    tipo: activityUi.filterTipo,
    rol: activityUi.listFilterRol,
    desde: activityUi.filterDesde,
    hasta: activityUi.filterHasta,
    apply: activityUi.filterApply,
    clear: activityUi.clear,
  };

  useEffect(() => {
    fetch(`${API_URL}/verificar-token`, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        if (data.valid) setRol(data.usuario.rol);
      })
      .catch((err) => console.error("Error obteniendo rol:", err));
  }, []);

  const handleVolver = () => {
    navigate(getHomeRouteByRole(rol), { replace: true });
  };

  const obtenerAcciones = async () => {
    try {
      const r = await fetch(`${API_URL}/acciones-sistema`, {
        credentials: "include",
      });
      const data = await r.json();
      if (r.ok) {
        setAcciones(unwrapAccionesListPayload(data));
        setSeleccionados([]);
      } else setMensaje(data.error || "Error al obtener acciones");
    } catch (e) {
      setMensaje("Error de conexión al obtener acciones");
    }
  };

  const obtenerSolicitudes = async () => {
    try {
      const r = await fetch(`${API_URL}/autorizaciones`, {
        credentials: "include",
      });
      const data = await r.json();
      if (r.ok) setSolicitudes(Array.isArray(data) ? data : []);
      else setMsgCursos(data.error || "Error al obtener solicitudes");
    } catch (e) {
      setMsgCursos("Error de conexión al obtener solicitudes");
    }
  };

  useEffect(() => {
    obtenerAcciones();
    obtenerSolicitudes();
  }, []);

  useEffect(() => {
    if (!modalObservacion.visible) {
      return;
    }
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setModalObservacion({ visible: false, accion: null });
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [modalObservacion.visible]);

  const toggleSeleccion = (accionId) => {
    setSeleccionados((prev) =>
      prev.includes(accionId) ? prev.filter((id) => id !== accionId) : [...prev, accionId],
    );
  };

  const verObservaciones = () => {
    if (seleccionados.length !== 1) {
      alert("Selecciona exactamente una acción para ver observaciones.");
      return;
    }
    const selectedId = seleccionados[0];
    const accion = accionesVisibles.find((a) => String(a._id) === String(selectedId));
    if (!accion) {
      setMensaje("La acción seleccionada ya no está en el listado. Se ha desmarcado la selección.");
      setSeleccionados([]);
      setModalObservacion({ visible: false, accion: null });
      return;
    }
    setModalObservacion({ visible: true, accion });
  };

  const enviarAccionMasiva = async (tipoRuta, confirmacion) => {
    if (seleccionados.length === 0) {
      alert("Selecciona al menos una acción.");
      return;
    }

    if (!window.confirm(confirmacion)) return;

    try {
      const { successCount, failCount, failures } = await postAccionesUsuarioBatch(
        seleccionados,
        tipoRuta,
      );
      if (failCount === 0) {
        alert("Acción realizada con éxito.");
      } else {
        const detalle = failures.map((f) => `id ${f.id}: ${f.message}`).join("\n");
        alert(
          `Completado parcialmente: ${successCount} con éxito, ${failCount} con error. Detalles (por id):\n${detalle}`,
        );
      }
      obtenerAcciones();
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      alert(`Error al procesar la acción: ${msg}`);
    }
  };

  const calcularDiasRestantes = (fechaExpiracion) => {
    if (!fechaExpiracion) return null;
    const ahora = new Date();
    const expiracion = new Date(fechaExpiracion);
    const diff = expiracion - ahora;
    if (diff <= 0) return "Cuenta finalizada";
    return Math.ceil(diff / (1000 * 60 * 60 * 24)) + " días restantes";
  };

  const decidirSolicitud = async (id, accion) => {
    try {
      const r = await fetch(`${API_URL}/autorizaciones/${id}/${accion}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({}),
      });
      let data = {};
      try {
        data = await r.json();
      } catch {
        data = {};
      }
      if (!r.ok) {
        const serverMsg =
          data && typeof data === "object" && typeof data.error === "string" && data.error.trim() !== ""
            ? data.error.trim()
            : "";
        const statusMsg = `Error ${r.status}${
          typeof r.statusText === "string" && r.statusText.trim() !== ""
            ? `: ${r.statusText.trim()}`
            : ""
        }`;
        alert(serverMsg || statusMsg || "Error al procesar la solicitud.");
        return;
      }
      await obtenerSolicitudes();
      const okDetail =
        data && typeof data === "object" && typeof data.mensaje === "string" && data.mensaje.trim() !== ""
          ? data.mensaje.trim()
          : "Solicitud procesada correctamente (aprobada o rechazada según la acción elegida).";
      alert(okDetail);
    } catch (e) {
      const detail = e instanceof Error ? e.message : String(e);
      alert(
        detail.trim() !== ""
          ? `Error al procesar la solicitud: ${detail}`
          : "Error al procesar la solicitud.",
      );
    }
  };

  return (
    <div className="a-container">
      <div className="a-main-container">
        <div className="a-content">
          <h1>Autorizaciones</h1>

          {acciones.length === 0 ? (
            <p>No hay actividades registradas.</p>
          ) : (
            <>
              <AccionesFiltroPanel
                draftFiltros={filtrosDraft}
                onDraftChange={setFiltrosDraft}
                onApply={handleApplyFiltros}
                onClear={handleClearFiltros}
                tipoOptions={tipoOptions}
                rolOptions={rolOptions}
                labels={filterPanelLabels}
              />
              {accionesVisibles.length === 0 ? (
                <p>{activityUi.empty}</p>
              ) : (
                <ActivityRecordsListScroller>
                  <div className="a-acciones-lista">
                    {accionesVisibles.map((accion) => {
                      const affectedLine = buildAccionAffectedProfileLine(accion, activityUi);
                      return (
                        <div key={accion._id} className="a-accion-card">
                          <input
                            type="checkbox"
                            checked={seleccionados.includes(accion._id)}
                            onChange={() => toggleSeleccion(accion._id)}
                          />
                          <h4>
                            {buildAccionCardHeadline(accion, getAccionSistemaTipoLabel, activityUi)}
                          </h4>
                          {affectedLine ? <p className="a-accion-affected">{affectedLine}</p> : null}
                          <p>
                            <strong>{activityUi.fecha}:</strong>{" "}
                            {formatActivityLogDateCell(accion.fecha || accion.createdAt)}
                          </p>
                          {accion.tipo === "eliminacion" && (
                            <p>
                              <strong>Cuenta atrás:</strong>{" "}
                              {calcularDiasRestantes(accion.fechaExpiracion)}
                            </p>
                          )}
                          <details className="a-accion-observaciones">
                            <summary>{activityUi.detailsSummary}</summary>
                            <p className="a-accion-observaciones-body">
                              {buildAccionDetailBody(accion, activityUi)}
                            </p>
                          </details>
                        </div>
                      );
                    })}
                  </div>
                </ActivityRecordsListScroller>
              )}
            </>
          )}
          <div className="a-content" style={{ marginTop: 30 }}>
            <h2>Autorizaciones de cursos (pendientes)</h2>
            {msgCursos && <p className="a-message">{msgCursos}</p>}
            {solicitudes.length === 0 ? (
              <p>No hay solicitudes pendientes.</p>
            ) : (
              <div className="a-acciones-lista">
                {solicitudes.map((s) => (
                  <div key={s._id} className="a-accion-card">
                    <h4>Curso: {getAutorizacionCursoLabel(s)}</h4>
                    <p>
                      <strong>Acción:</strong> {s.accion}
                    </p>
                    <p>
                      <strong>Campo:</strong> {s.payload?.campo}
                    </p>
                    <p>
                      <strong>De → A:</strong> {s.payload?.anterior ?? "—"} → {s.payload?.nuevo ?? "—"}
                    </p>
                    <p>
                      <strong>Solicitante:</strong> {s.solicitante?.nombre} ({s.solicitante?.rol})
                    </p>
                    <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                      <button
                        className="a-sidebar-button"
                        onClick={() => decidirSolicitud(s._id, "aprobar")}
                      >
                        Aprobar
                      </button>
                      <button
                        className="a-sidebar-button"
                        onClick={() => decidirSolicitud(s._id, "rechazar")}
                      >
                        Rechazar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {mensaje && <p className="a-message">{mensaje}</p>}
        </div>

        <ResponsiveActionSidebar
          id="autorizaciones-sidebar"
          className="a-sidebar"
          openClassName="a-sidebar--open"
          ariaLabel="Acciones de autorizaciones"
        >
          <h4>Acciones</h4>
          <ul className="a-sidebar-links">
            <li>
              <button className="a-sidebar-button" onClick={verObservaciones}>
                Ver Observaciones
              </button>
            </li>
            <li>
              <button
                className="a-sidebar-button"
                onClick={() =>
                  enviarAccionMasiva("reactivar", "¿Seguro que deseas reactivar los seleccionados?")
                }
              >
                Reactivar
              </button>
            </li>
            <li>
              <button
                className="a-sidebar-button"
                onClick={() =>
                  enviarAccionMasiva("bloquear", "¿Seguro que deseas bloquear los seleccionados?")
                }
              >
                Bloquear
              </button>
            </li>
            {rol === "director" && (
              <li>
                <button
                  className="a-sidebar-button"
                  onClick={() =>
                    enviarAccionMasiva(
                      "eliminar-definitivo",
                      "¿Eliminar definitivamente los seleccionados? Esto no se puede deshacer.",
                    )
                  }
                >
                  Eliminar Definitivamente
                </button>
              </li>
            )}
            <li>
              <button className="mi-sidebar-button staff-sidebar-exit-button" onClick={handleVolver}>
                Salir
              </button>
            </li>
          </ul>
        </ResponsiveActionSidebar>
      </div>

      {modalObservacion.visible && modalObservacion.accion && (
        <div
          className="modal-observacion-backdrop"
          onClick={() => setModalObservacion({ visible: false, accion: null })}
        >
          <div className="modal-observacion" onClick={(e) => e.stopPropagation()}>
            <h3>Observaciones</h3>
            <p>
              <strong>Usuario afectado:</strong>{" "}
              {(modalObservacion.accion.idRecursoSnapshot?.nombre ??
                modalObservacion.accion.idRecurso?.nombre) ||
                "Desconocido"}
            </p>
            <p>
              <strong>Rol:</strong>{" "}
              {(modalObservacion.accion.idRecursoSnapshot?.rol ??
                modalObservacion.accion.idRecurso?.rol) ||
                "N/A"}
            </p>
            <p>
              <strong>Acción:</strong> {modalObservacion.accion.tipo}
            </p>
            <div className="modal-observacion-field">
              <strong>Observación:</strong>
              <div className="modal-observacion-text">
                {typeof modalObservacion.accion.observacion === "string" &&
                modalObservacion.accion.observacion.trim() !== ""
                  ? modalObservacion.accion.observacion
                  : "Sin observaciones."}
              </div>
            </div>
            <button
              className="a-btn-cerrar"
              onClick={() => setModalObservacion({ visible: false, accion: null })}
            >
              Cerrar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Autorizaciones;
